import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import {
  Clock,
  ExternalLink,
  Globe,
  Grid,
  Info,
  Instagram,
  List,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  ShoppingBag,
  User,
} from 'react-feather';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import TrackedCouponCard from 'components/TrackedCouponCard/TrackedCouponCard';
import { CustomerCouponCard, SeoHead, Spinner } from 'components';
// ASSUMPTION: SEOHead's SITE_URL is imported directly from the component
// file rather than re-exported through the 'components' barrel — confirm
// 'components/SEOHead' actually resolves (matches wherever the file lives)
// or adjust this path/alias to whatever your barrel re-exports. Same
// assumption used in CouponsDetails.js.
import LoginPrompt from 'components/LoginPrompt/LoginPrompt';
import ScrollRail from 'components/ScrollRail/ScrollRail';
import { SITE_URL } from 'components/SeoHead/SeoHead';
import { COUPON_TYPES } from 'coupon-common';
import { actions as couponActions, duck as businessCouponsDuck } from 'coupon-common/src/modules/businesses/coupons';
import { actions, duck as businessDetailsDuck } from 'coupon-common/src/modules/businesses/details';
import { actions as followActions } from 'coupon-common/src/modules/businesses/follow';
// Same cached slice Home/GuestHome/CouponsDetails already populate — reused
// here (not a dedicated request) for the "Similar coupons" rail at the
// bottom of the page. See CouponsDetails.js for the same pattern.
import { actions as allCouponsActions } from 'coupon-common/src/modules/customerCoupons/all';
import { actions as favCouponActions } from 'coupon-common/src/modules/customerCoupons/favourite';
import { dispatchAsAsync } from 'coupon-common/src/utils';

import NoImage from '../../../assets/images/NoCouponImage.webp';
import { RouteNames } from '../../../constants';
import { resolveImageUrl } from '../../../utils/cleanExternalUrls';
import styles from './BusinessesDetails.module.scss';
import messages from './messages';

// Same amount/percentage/points-back switch CouponsDetails.js uses for its
// own Similar Coupons rail — pulled out here so this page's copy of that
// rail reads identically.
const getCouponDealAmount = coupon => {
  if (!coupon) return null;
  if (coupon.type === COUPON_TYPES.AMOUNT_OFF) return `$${coupon.amount_off} OFF`;
  if (coupon.type === COUPON_TYPES.PERCENTAGE_OFF) return `${Math.round(coupon.percentage_off)}% OFF`;
  if (coupon.type === COUPON_TYPES.POINTS_BACK) return `${coupon.points_back} Points Back`;
  return null;
};

// How many of this business's own coupons show before the "More" button.
// Multiple of the grid's 3 columns, so each "more" batch fills whole rows.
const COUPONS_PAGE_SIZE = 6;

const BUSINESS_DETAIL_INITIAL_STATE = businessDetailsDuck.BUSINESS_DETAIL_INITIAL_STATE;
const BUSINESS_COUPONS_INITIAL_STATE = businessCouponsDuck.BUSINESS_COUPONS_INITIAL_STATE;

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const GOOGLE_MAPS_LIBRARIES = [];

// TODO: Remove once the legacy DB lat/lon swap is corrected. All locations
// are in the northern hemisphere, so a negative latitude means the pair was
// stored in reverse order — swap them back. Same check used in
// CouponsMapAll.js.
const normalizeHemisphere = ({ latitude, longitude }) => {
  if (latitude < 0) {
    return { latitude: longitude, longitude: latitude };
  }
  return { latitude, longitude };
};

/**
 * Business coordinates are stored as [latitude, longitude] for this
 * business — matches the ordering openMapOnLocation already assumed below,
 * kept consistent rather than re-deriving a different convention here.
 */
const resolveCoordinate = location => {
  const coords = location?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const [rawLatitude, rawLongitude] = coords;
  if (!Number.isFinite(rawLatitude) || !Number.isFinite(rawLongitude)) return null;
  const { latitude, longitude } = normalizeHemisphere({ latitude: rawLatitude, longitude: rawLongitude });
  return { lat: latitude, lng: longitude };
};

/**
 * Normalizes a social handle/URL into a clickable link. Accepts either a
 * full URL or a bare handle (e.g. "mannyo" for Instagram) — if it's not
 * already a URL, builds one against the given platform's base.
 */
const toSocialUrl = (value, base) => {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  const handle = value.replace(/^@/, '');
  return `${base}${handle}`;
};

const toWhatsAppUrl = phoneOrUrl => {
  if (!phoneOrUrl) return null;
  if (/^https?:\/\//i.test(phoneOrUrl)) return phoneOrUrl;
  const digitsOnly = phoneOrUrl.replace(/[^\d]/g, '');
  return digitsOnly ? `https://wa.me/${digitsOnly}` : null;
};

/**
 * Web port of Businesses/BusinessesDetails.
 * Two-column layout: business profile / location info on the left,
 * active deals (grid or list) on the right.
 *
 * DESIGN UPDATE: reworked to match the reference — bigger hero avatar,
 * two rows of contact/social quick-action icons (website/phone/directions,
 * then Instagram/Telegram/WhatsApp) positioned beside the overlapping
 * avatar circle (the Business Info card header's edit button has since
 * been removed), an
 * Info-icon "About" tab, and an added "contact/owner name" row. The three
 * new social fields (instagram, telegram, whatsapp) and the owner/contact
 * name aren't in the data this component destructured before — added as
 * optional (`data.instagram_url` etc., best-guess API field names) so
 * nothing breaks if the backend doesn't send them yet; each renders
 * conditionally. Confirm/rename against your actual API once known.
 *
 * LAYOUT UPDATE: heroText (business name + tagline) is a sibling AFTER
 * heroCard closes, not nested inside it — see BusinessesDetails.module.scss
 * for the full history/reasoning on that one.
 *
 * DEALS UPDATE: the "Active Deals" grid/list used to be built from scratch
 * here (dealCard/dealListRow markup + local getDealAmount/getOriginalPrice/
 * getCouponImage helpers) — that duplicated CustomerCouponCard almost
 * entirely and never got favourite/heart-button support because of it.
 * Both view modes now render <CustomerCouponCard>, same component Home.js
 * uses, with layout="grid" | "list" switching its internal markup. The
 * business header (logo + name) is shown per-card here, same as on Home —
 * even though every coupon on this page belongs to the one business at the
 * top of the page, showing it per-card was explicitly requested. Favourite
 * state/handlers are ported from Home.js's pattern (allFavourite selector,
 * favCouponActions, checkFavourite/toggleFavouriteHandler) since this page
 * never had that wiring before — the old hand-built cards didn't render a heart button at
 * all.
 *
 * FOLLOW BUTTON — ported from CouponsDetails.js's businessPanel (same
 * pattern already used for AffiliateBusinessDetails.js). This page had NO
 * follow wiring at all before — no import, no selector, nothing. Added:
 * followState selector + followRecord (the matched record, not just a
 * bool — unfollowBusinessRequest needs its own `id`) + toggleFollowHandler
 * + getFollowedBusinessesRequest on mount. Unlike AffiliateBusinessDetails
 * (which passes `is_affiliate_or_flyer: true` unconditionally, since every
 * business on that route already is one), this page reads the real
 * `isAffiliateOrFlyer` value off `data` — a regular business here could
 * still be flagged as affiliate/flyer depending on how it was created.
 * Rendered at the bottom of the Business Info card (below the about rows),
 * same placement as on AffiliateBusinessDetails.
 *
 * LOGIN GATE (new): toggleFollowHandler now checks the same `authorized`
 * flag CouponAction.js/CouponsDetails.js already use, and opens
 * loginPromptVisible instead of dispatching when the visitor is a guest —
 * same shape as the gate CouponsDetails.js's own page-level Follow button
 * got. Favourite/Follow on the individual coupon cards in the right column
 * don't need any gating added here: CustomerCouponCard now checks
 * `authorized` and owns its own login-prompt internally, regardless of
 * which page embeds it.
 *
 * SEO (new): SEOHead replaces the old static Helmet block, built from the
 * same `name`/`tagline`/`coverImage`/`address1`/`address2`/`disclosure`
 * values already computed below — no new fetches. JSON-LD uses LocalBusiness
 * (not Offer, which CouponsDetails.js uses) since this page represents the
 * business itself, not a single deal. Only rendered in the successful-
 * render return; the error/loading early returns fall back to SEOHead's
 * site-wide defaults since those states are transient.
 */
const BusinessesDetails = () => {
  const { id: idParam } = useParams();
  const id = idParam ? Number(idParam) : null;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();

  const [activeTab, setActiveTab] = useState('about');
  const [viewMode, setViewMode] = useState('grid');
  const [loginPromptVisible, setLoginPromptVisible] = useState(false);
  const [visibleCouponsCount, setVisibleCouponsCount] = useState(COUPONS_PAGE_SIZE);

  // List view is desktop-only — it needs the extra horizontal room the
  // wide row layout assumes; on tablet/mobile it doesn't have that room
  // and doesn't make sense. isSmallScreen mirrors .layout's own existing
  // breakpoint (900px, where the two-column layout already stacks to
  // one) rather than inventing a new one. This is checked on mount and on
  // resize, not just once, so resizing an already-open desktop tab down
  // past 900px also forces it back to grid immediately (see
  // effectiveViewMode below), not just on next page load.
  const [isSmallScreen, setIsSmallScreen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 900,
  );

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 900px)');
    const handleChange = e => setIsSmallScreen(e.matches);
    handleChange(mql);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // What actually gets passed to CustomerCouponCard / used to pick the
  // wrapper class — never 'list' below the breakpoint, regardless of what
  // the (still-remembered) viewMode state says, so switching to list on
  // desktop and then shrinking the window can't leave list view stuck on
  // a screen too narrow for it.
  const effectiveViewMode = isSmallScreen ? 'grid' : viewMode;

  const businessDetailsState = useSelector(state => state.businesses.details);
  const businessDetailState = (id && businessDetailsState?.[id]) || BUSINESS_DETAIL_INITIAL_STATE;
  const { fetching, fetched, data, error } = businessDetailState;

  // Coupons for a business are served by a dedicated endpoint (same one
  // CouponsMapAll uses for its bottom-sheet list), not embedded in the
  // business-detail response — so they're fetched and selected separately.
  const businessCouponsAllState = useSelector(state => state.businesses.coupons);
  const businessCouponsState = (id && businessCouponsAllState?.[id]) || BUSINESS_COUPONS_INITIAL_STATE;
  const { fetching: couponsFetching, fetched: couponsFetched, data: couponsData, error: couponsError } = businessCouponsState;
  // API response shape is `{ coupons: [...] }` (confirmed via the debug log
  // below — `.results` and bare-array were guesses that didn't match what
  // this endpoint actually sends). Checked first; `.results` and bare array
  // kept as fallbacks in case other callers of this same duck ever get a
  // differently-shaped response.
  const coupons = Array.isArray(couponsData)
    ? couponsData
    : couponsData?.coupons || couponsData?.results || [];
  const visibleCoupons = useMemo(() => coupons.slice(0, visibleCouponsCount), [coupons, visibleCouponsCount]);

  // Reset back to the first page whenever the business itself changes —
  // otherwise navigating from one business's page to another (e.g. via the
  // Similar Coupons rail) could leave a stale, larger count applied to a
  // business whose own coupon list hasn't loaded yet.
  useEffect(() => {
    setVisibleCouponsCount(COUPONS_PAGE_SIZE);
  }, [id]);

  // useEffect(() => {
  //   // eslint-disable-next-line no-console
  //   console.log('[BusinessesDetails] businesses/coupons response for business', id, couponsData);
  // }, [id, couponsData]);

  // ─── Similar coupons (other businesses) ───────────────────────────────
  // Same cached customerCoupons/all slice Home/GuestHome/CouponsDetails.js
  // already populate — reused as-is rather than a dedicated request, and
  // fetched once here as a fallback if a visitor lands directly on this
  // page with nothing cached yet.
  const customerCouponsAllState = useSelector(state => state.customerCoupons.all || {});
  const { items: allCouponItems, result: allCouponResult, fetching: allCouponsFetching } = customerCouponsAllState;
  const activeCityId = useSelector(state => (state.cities && state.cities.activeCityId) || null);

  // "Similar coupons" means the same category(ies) this business's own
  // coupons belong to — same id/name matching CustomerCouponListAll/
  // Favourites already use for category filtering. Falls back to the
  // unfiltered (business-excluded) list when this business's coupons have
  // no category to match against.
  const businessCategoryKeys = useMemo(() => {
    const keys = coupons
      .map(item => item.category?.id ?? (item.category?.name || (typeof item.category === 'string' ? item.category : null)))
      .filter(Boolean);
    return new Set(keys);
  }, [coupons]);

  const similarCoupons = useMemo(() => {
    const ids = allCouponResult?.results || [];
    const otherBusinessCoupons = ids
      .map(rid => allCouponItems?.[rid])
      .filter(item => item && item.business?.id !== id);

    if (businessCategoryKeys.size === 0) return otherBusinessCoupons.slice(0, 10);

    return otherBusinessCoupons
      .filter(item => {
        const itemCategoryKey = item.category?.id ?? (item.category?.name || (typeof item.category === 'string' ? item.category : null));
        return itemCategoryKey && businessCategoryKeys.has(itemCategoryKey);
      })
      .slice(0, 10);
  }, [allCouponResult, allCouponItems, id, businessCategoryKeys]);

  const hasFetchedSimilar = useRef(false);
  useEffect(() => {
    if (hasFetchedSimilar.current) return;
    if (similarCoupons.length > 0 || allCouponsFetching) return;
    hasFetchedSimilar.current = true;
    dispatch(allCouponsActions.getAllList({ page: 1, ...(activeCityId ? { city: activeCityId } : {}) }));
  }, [similarCoupons.length, allCouponsFetching, activeCityId, dispatch]);

  // Favourite state — same shape/selector Home.js uses
  // (state.customerCoupons.favourite), so CustomerCouponCard's
  // isFavourite/toggleFavourite props behave identically on both pages.
  const allFavourite = useSelector(state => state.customerCoupons.favourite);
  const favoriteList = allFavourite?.fetched ? allFavourite?.data?.results || [] : [];
  const checkFavourite = useCallback(coupon => favoriteList.some(f => f.id === coupon.id), [favoriteList]);

  const toggleFavouriteHandler = useCallback(
    (couponId, isFavourite) => {
      const action = isFavourite ? favCouponActions.removeFavourite(couponId) : favCouponActions.markAsFavourite(couponId);
      dispatchAsAsync(action, dispatch).then(() => {
        dispatch(favCouponActions.getAllFavouriteList());
      });
    },
    [dispatch],
  );

  // Same auth flag CouponAction.js/CouponsDetails.js read for their own
  // gating.
  const authorized = useSelector(state => !!(state.auth && state.auth.authorized));

  // ─── Follow state — same pattern as CouponsDetails.js / AffiliateBusinessDetails.js ───
  const followState = useSelector(state => state.businesses.follow);
  const followRecord = useMemo(
    () => (followState?.data?.results || []).find(b => b.business_id === id),
    [followState?.data?.results, id],
  );
  const isBusinessFollowed = !!followRecord;

  const toggleFollowHandler = useCallback(() => {
    if (!authorized) {
      setLoginPromptVisible(true);
      return;
    }
    if (!id) return;
    if (isBusinessFollowed) {
      dispatch(
        followActions.unfollowBusinessRequest({
          id: followRecord?.id,
          business_id: id,
          is_affiliate_or_flyer: !!data?.is_affiliate_or_flyer,
        }),
      );
    } else {
      dispatch(
        followActions.followBusinessRequest({
          business_id: id,
          is_affiliate_or_flyer: !!data?.is_affiliate_or_flyer,
        }),
      );
    }
  }, [authorized, dispatch, id, isBusinessFollowed, followRecord, data?.is_affiliate_or_flyer]);

  // ─── Fetch Business Detail + Coupons + Follow list + Favourites ───────
  const getBusinessDetail = useCallback(() => {
    if (id) dispatch(actions.getDetail(id));
  }, [dispatch, id]);

  const getBusinessCoupons = useCallback(() => {
    if (id) dispatch(couponActions.getBusinessCoupons(id));
  }, [dispatch, id]);

  useEffect(() => {
    getBusinessDetail();
    getBusinessCoupons();
  }, [getBusinessDetail, getBusinessCoupons]);

  useEffect(() => {
    dispatch(favCouponActions.getAllFavouriteList());
    dispatch(followActions.getFollowedBusinessesRequest());
  }, [dispatch]);

  // ─── Utility Action Handlers ─────────────────────────────────────────
  const openPhoneNumber = phone => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  const handleVisitWebsite = url => {
    if (url) {
      const withScheme = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      window.open(withScheme, '_blank', 'noopener,noreferrer');
    }
  };

  const openExternalLink = url => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const coordinate = resolveCoordinate(data?.location);

  // Navigates to the in-app coupons map, centered on this exact business —
  // used by both the "Directions" quick-action icon on the hero photo and
  // the embedded map preview in the "Get Direction" card below. Reuses the
  // already-validated `coordinate` (via resolveCoordinate) instead of
  // re-reading data.location.coordinates directly, so there's one single
  // source of truth for the lat/lng ordering assumption noted above.
  const openMapOnLocation = () => {
    if (!coordinate) return;
    navigate(RouteNames.COUPONS_MAPS_ALL, {
      state: { dest: { latitude: coordinate.lat, longitude: coordinate.lng } },
    });
  };

  // Opens the device's actual Google Maps app/site for turn-by-turn
  // directions — distinct from openMapOnLocation, which stays inside this
  // app. Used only by the explicit "Open in Google Maps" header action.
  const handleOpenExternalDirections = () => {
    if (!coordinate) return;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${coordinate.lat},${coordinate.lng}&travelmode=driving`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const { isLoaded: isMapLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const goToCoupon = couponId => navigate(`${RouteNames.COUPONS_DETAILS}/${couponId}`);
  // Every coupon on this page already belongs to `id` (this business), so
  // clicking the logo/name on a card just re-navigates to this same page —
  // harmless (React Router won't re-fetch for a same-route navigation),
  // but worth knowing this button doesn't go anywhere new in practice.
  const goToThisBusiness = () => navigate(`${RouteNames.BUSINESSES_DETAILS}/${id}`);

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.wrap}>
          <p className={styles.stateText}>
            {typeof error === 'string' ? error : 'Something went wrong loading this business profile.'}
          </p>
        </div>
      </div>
    );
  }

  if (fetching && !fetched) {
    return (
      <div className={styles.page}>
        <div className={styles.wrap} style={{ display: 'flex', justifyContent: 'center', paddingTop: '6rem' }}>
          <Spinner show size="lg" />
        </div>
      </div>
    );
  }

  const {
    name,
    logo,
    cover_image: coverImage,
    categories = [],
    is_affiliate_or_flyer: isAffiliateOrFlyer,
    disclosure,
    phone,
    website_url: websiteUrl,
    street,
    city,
    postal_code: postalCode,
    // New fields (see file-level comment) — best-guess names, confirm
    // against the actual API response.
    owner_name: ownerName,
    instagram_url: instagramUrl,
    telegram_url: telegramUrl,
    whatsapp_number: whatsappNumber,
  } = data || {};

  const address1 = street || '';
  const address2 = `${city?.name || ''}${postalCode ? `, ${postalCode}` : ''}`;
  const tagline = categories.length > 0 ? categories.map(cat => cat.name || cat).join(' · ') : null;

  const instagramHref = toSocialUrl(instagramUrl, 'https://instagram.com/');
  const telegramHref = toSocialUrl(telegramUrl, 'https://t.me/');
  const whatsappHref = toWhatsAppUrl(whatsappNumber);

  // ─── SEO ────────────────────────────────────────────────────────────

  const businessPath = `${RouteNames.BUSINESSES_DETAILS}/${id}`;
  const cityName = city?.name;
  const seoTitle = name
    ? `${name} Deals & Coupons${cityName ? ` in ${cityName}` : ''} | My Couponbook`
    : undefined;
    const seoDescription = name
      ? `Find the latest deals and discounts for ${name}${cityName ? ` in ${cityName}` : ''}. Save money while supporting your favourite local businesses.`
      : undefined;

  // const seoDescription = data
  //   ? `${name ? `${name} — ` : ''}${tagline ? `${tagline}. ` : ''}${
  //       address2 ? `Located in ${address2}. ` : ''
  //     }Find deals and coupons from ${name || 'this business'} on My Couponbook.`
  //   : undefined;
  // geo/sameAs reuse `coordinate` and `instagramHref`/`telegramHref`/
  // `whatsappHref`, both already computed above for the map card and the
  // social icon row — no new data needed for either.
  // TODO: openingHoursSpecification is left out on purpose — this page has
  // no hours data at all right now (the "Hours" tab above just renders
  // "Store hours are not available for this business yet"). Add it here
  // once the API actually returns hours and a real tab replaces that
  // placeholder.
  const sellerSameAs = [instagramHref, telegramHref, whatsappHref].filter(Boolean);

  const structuredData = data
    ? {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name,
        image: coverImage || logo || undefined,
        logo: logo || undefined,
        telephone: phone || undefined,
        url: `${SITE_URL}${businessPath}`,
        address:
          address1 || address2
            ? {
                '@type': 'PostalAddress',
                streetAddress: address1 || undefined,
                addressLocality: city?.name || undefined,
                postalCode: postalCode || undefined,
              }
            : undefined,
        geo: coordinate
          ? {
              '@type': 'GeoCoordinates',
              latitude: coordinate.lat,
              longitude: coordinate.lng,
            }
          : undefined,
        sameAs: sellerSameAs.length > 0 ? sellerSameAs : undefined,
      }
    : undefined;

  return (
    <div className={styles.page}>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        image={coverImage || logo}
        path={businessPath}
        type="profile"
        structuredData={structuredData}
      />
      <div className={styles.wrap}>
        <div className={styles.layout}>
          {/* ─── Left column: profile, info, directions ─── */}
          <div className={styles.leftCol}>
            {/* Hero business card — image + avatar + social icons +
                affiliate disclosure. heroText is NOT in here anymore. */}
            <div className={styles.heroCard}>
              <div className={styles.heroImageWrap}>
                <div className={styles.heroImageClip}>
                  <img
                    src={coverImage || 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=1200&q=80'}
                    alt={`${name || 'Business'} cover`}
                    className={styles.heroImage}
                  />
                  <div className={styles.heroOverlay} />

                  <div className={styles.heroIconsRow}>
                    {websiteUrl && (
                      <button type="button" className={styles.iconBtn} onClick={() => handleVisitWebsite(websiteUrl)} title="Website">
                        <Globe size={17} />
                      </button>
                    )}
                    {phone && (
                      <button type="button" className={styles.iconBtn} onClick={() => openPhoneNumber(phone)} title="Call Store">
                        <Phone size={16} />
                      </button>
                    )}
                    <button type="button" className={styles.iconBtn} onClick={openMapOnLocation} title="Directions">
                      <MapPin size={17} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Sibling of heroImageWrap, not nested inside it — anchored
                  to .heroCard's own bottom edge (which has
                  overflow: visible) so exactly half the circle sits below
                  the card's boundary, regardless of how tall the card ends
                  up being (icon row / affiliate badge / disclosure all
                  vary the card's total height). */}
              <div className={styles.logoRing}>
                {logo ? (
                  <img src={logo} alt={name || ''} className={styles.logoCircle} />
                ) : (
                  <div className={styles.logoFallback}>{name?.charAt(0) || 'B'}</div>
                )}
              </div>

              {(instagramHref || telegramHref || whatsappHref) && (
                <div className={styles.socialIconsRow}>
                  {instagramHref && (
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => openExternalLink(instagramHref)}
                      title="Instagram"
                    >
                      <Instagram size={16} />
                    </button>
                  )}

                  {telegramHref && (
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => openExternalLink(telegramHref)}
                      title="Telegram"
                    >
                      <Send size={16} />
                    </button>
                  )}

                  {whatsappHref && (
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => openExternalLink(whatsappHref)}
                      title="WhatsApp"
                    >
                      <MessageCircle size={16} />
                    </button>
                  )}
                </div>
              )}

              {isAffiliateOrFlyer && <span className={styles.affiliateBadge}>Affiliate Partner</span>}
              {isAffiliateOrFlyer && disclosure && <p className={styles.disclosure}>{disclosure}</p>}
            </div>

            {/* heroText — sibling of heroCard, NOT nested inside it, so it
                has none of .card's white background/shadow/border-radius;
                just plain text sitting on .page's own background, pulled
                up via heroCard's reduced margin-bottom to sit beside the
                avatar's lower portion instead of starting well below it. */}
            <div className={styles.heroText}>
              <h1 className={styles.heroName}>{name}</h1>
              {tagline && <p className={styles.heroTagline}>{tagline}</p>}
            </div>

            {/* Business Info card: About / Hours */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <ShoppingBag size={18} className={styles.cardHeaderIcon} />
                <h2 className={styles.cardHeaderTitle}>Business Info</h2>
              </div>

              {/* <div className={styles.tabRow}>
                <button
                  type="button"
                  className={`${styles.tabBtn} ${activeTab === 'about' ? styles.active : ''}`}
                  onClick={() => setActiveTab('about')}
                >
                  <Info size={14} /> About
                </button>
                <button
                  type="button"
                  className={`${styles.tabBtn} ${activeTab === 'hours' ? styles.active : ''}`}
                  onClick={() => setActiveTab('hours')}
                >
                  <Clock size={14} /> Hours
                </button>
              </div> */}

              {activeTab === 'about' ? (
                <div className={styles.aboutList}>
                  {name && (
                    <div className={styles.aboutRow}>
                      <span className={styles.aboutIcon}>
                        <ShoppingBag size={16} />
                      </span>
                      <span className={styles.aboutValue}>{name}</span>
                    </div>
                  )}
                  {ownerName && (
                    <div className={styles.aboutRow}>
                      <span className={styles.aboutIcon}>
                        <User size={16} />
                      </span>
                      <span className={styles.aboutValue}>{ownerName}</span>
                    </div>
                  )}
                  {phone && (
                    <div className={styles.aboutRow}>
                      <span className={styles.aboutIcon}>
                        <Phone size={16} />
                      </span>
                      <span className={styles.aboutValue}>{phone}</span>
                    </div>
                  )}
                  {(address1 || address2) && (
                    <div className={styles.aboutRow}>
                      <span className={styles.aboutIcon}>
                        <MapPin size={16} />
                      </span>
                      <span className={styles.aboutValue}>
                        {address1}
                        {address1 && address2 ? ', ' : ''}
                        {address2}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.hoursEmpty}>Store hours are not available for this business yet.</div>
              )}

              {/* Follow / Following — bottom of Business Info, same
                  placement as AffiliateBusinessDetails.js. Wrapper carries
                  the same horizontal/bottom padding .aboutList uses so it
                  lines up with the rows above instead of touching the
                  card's edges. */}
              <div className={styles.followBtnWrap}>
                <button
                  type="button"
                  className={isBusinessFollowed ? styles.followBtn : `${styles.followBtn} ${styles.notFollowing}`}
                  onClick={toggleFollowHandler}
                >
                  {isBusinessFollowed ? 'Following' : 'Follow'}
                </button>
              </div>
            </div>

            {/* Get Direction card */}
            {coordinate && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <MapPin size={18} className={styles.cardHeaderIcon} />
                  <h2 className={styles.cardHeaderTitle}>Get Direction</h2>
                  <button
                    type="button"
                    className={styles.cardHeaderAction}
                    onClick={handleOpenExternalDirections}
                    title="Open in Google Maps"
                  >
                    <ExternalLink size={19} />
                  </button>
                </div>

                <div className={styles.mapFrame}>
                  {isMapLoaded && GOOGLE_MAPS_API_KEY ? (
                    <GoogleMap
                      center={coordinate}
                      zoom={14}
                      options={{ disableDefaultUI: true, gestureHandling: 'cooperative' }}
                      mapContainerStyle={{ width: '100%', height: '100%' }}
                      onClick={openMapOnLocation}
                    >
                      <Marker position={coordinate} onClick={openMapOnLocation} />
                    </GoogleMap>
                  ) : (
                    <button type="button" className={styles.mapFallback} onClick={openMapOnLocation}>
                      <MapPin size={18} />
                      &nbsp;Tap to view on the map
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ─── Right column: Active Deals ─── */}
          <div className={styles.rightCol}>
            <div className={styles.dealsHeader}>
              <h2 className={styles.dealsTitle}>Coupons</h2>
              <div className={styles.viewToggle}>
                <button
                  type="button"
                  className={`${styles.viewToggleBtn} ${viewMode === 'grid' ? styles.active : ''}`}
                  onClick={() => setViewMode('grid')}
                >
                  <Grid size={14} /> Grid
                </button>
                <button
                  type="button"
                  className={`${styles.viewToggleBtn} ${styles.listToggleBtn} ${viewMode === 'list' ? styles.active : ''}`}
                  onClick={() => setViewMode('list')}
                >
                  <List size={14} /> List
                </button>
              </div>
            </div>

            {couponsFetching && !couponsFetched ? (
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '2rem' }}>
                <Spinner show size="md" />
              </div>
            ) : coupons.length === 0 ? (
              <p className={styles.stateText}>
                {couponsError
                  ? 'Something went wrong loading coupons for this business.'
                  : intl.formatMessage(messages.noCouponsMessage)}
              </p>
            ) : (
              <>
                {name && <h3 className={styles.subDealsTitle}>Top {name} Deals</h3>}
                <div className={effectiveViewMode === 'grid' ? styles.dealsGrid : styles.dealsListWrap}>
                {coupons.map((coupon, index) => (
                  <TrackedCouponCard
                    key={coupon.id}
                    item={coupon}
                    index={index}
                    feedId="business_deals"
                    feedSort={effectiveViewMode}
                    businessId={id}
                    first={index === 0}
                    layout={effectiveViewMode}
                    onPress={() => goToCoupon(coupon.id)}
                    goToBusinessDetails={goToThisBusiness}
                    isFavourite={checkFavourite(coupon)}
                    toggleFavourite={() => toggleFavouriteHandler(coupon.id, checkFavourite(coupon))}
                    business={{ id, name, logo, city }}
                     variant="business"
                  />
                ))}
              </div>
              </>
            )}
          </div>
        </div>

        {/* ─── Similar coupons (other businesses) ─── */}
        {similarCoupons.length > 0 && (
          <div className={styles.similarSection}>
            <h2 className={styles.similarTitle}>Similar coupons</h2>
            <ScrollRail className={styles.similarScroll} ariaLabel="similar coupons">
              {similarCoupons.map(item => {
                const itemImage = resolveImageUrl(item.image_url?.[0]?.image_url) || NoImage;
                const itemCategoryName = item.category?.name || (typeof item.category === 'string' ? item.category : null);
                const itemDealAmount = getCouponDealAmount(item);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={styles.similarCard}
                    onClick={() => navigate(`${RouteNames.COUPONS_DETAILS}/${item.id}`)}
                  >
                    <img src={itemImage} alt="" className={styles.similarCardImg} />
                    <div className={styles.similarCardOverlay} />
                    <div className={styles.similarBadgeRow}>
                      {itemCategoryName && <span className={styles.badgePillWhite}>{itemCategoryName}</span>}
                      {item.is_exclusive_paid && <span className={styles.badgePillGold}>Premium</span>}
                    </div>
                    <div className={styles.similarCardText}>
                      <p className={styles.similarCardTitle}>{itemDealAmount || item.title}</p>
                      {itemDealAmount && <p className={styles.similarCardSubtitle}>{item.title}</p>}
                    </div>
                  </button>
                );
              })}
            </ScrollRail>
          </div>
        )}
      </div>

      <LoginPrompt
        isOpen={loginPromptVisible}
        onClose={() => setLoginPromptVisible(false)}
      />
    </div>
  );
};

export default BusinessesDetails;